import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  FOLDER_NAME_CONFLICT_MESSAGE,
  type ListFoldersResult,
  type Project,
  type ProjectFolder,
  normalizeFolderName,
} from '@terafile/shared'
import { useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  ActiveProjectsExplorer,
  LocationProjectsList,
} from '../components/ActiveProjectsExplorer.js'
import { DeleteFolderModal } from '../components/DeleteFolderModal.js'
import { DeleteProjectModal } from '../components/DeleteProjectModal.js'
import { MoveToTrashModal, type TrashTarget } from '../components/MoveToTrashModal.js'
import { ApiError } from '../lib/api.js'
import { useAppShell } from '../lib/app-shell-context.js'
import { FETCH_ERROR_FALLBACK, SUBMIT_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { listFiles } from '../lib/files-api.js'
import { createFolder, deleteFolder, listFolders, updateFolder } from '../lib/folders-api.js'
import { LOADING, TRASH_ACTION } from '../lib/labels.js'
import { useMeTenant } from '../lib/me-api.js'
import {
  archiveProject,
  getProject,
  projectListQueryOptions,
  unarchiveProject,
} from '../lib/projects-api.js'
import {
  type ProjectsLocation,
  parseProjectsLocation,
  projectsLocationHref,
} from '../lib/projects-location.js'
import { useToast } from '../lib/toast.js'

/**
 * プロジェクト一覧。サイドバーの「場所」に応じてメインの中身を切り替える。
 */
export function ProjectsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = parseProjectsLocation(searchParams)
  const page = useProjectsPageModel(location, setSearchParams)
  const currentFolder = page.currentFolder
  const renameTarget = page.renameTarget

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-6 md:py-8 space-y-5">
      <LocationToolbar
        title={page.title}
        count={page.count}
        location={location}
        onCreateProject={location.kind === 'trash' ? undefined : page.createInCurrentLocation}
        onStartCreateFolder={
          location.kind === 'all' && !page.foldersUnavailable
            ? () => page.setCreatingFolder(true)
            : undefined
        }
        onRenameFolder={
          currentFolder ? () => setRenameFromFolder(page.setRenameTarget, currentFolder) : undefined
        }
        onDeleteFolder={
          currentFolder
            ? () => setDeleteFromFolder(page.setFolderDeleteTarget, currentFolder)
            : undefined
        }
      />

      {page.isLoading && <p className="text-sm text-muted-foreground">{LOADING.fetching}</p>}
      {page.error && <ErrorBanner message={toUserMessage(page.error, FETCH_ERROR_FALLBACK)} />}

      <ProjectsMainContent location={location} page={page} />

      <MoveToTrashModal
        target={page.trashTarget}
        onClose={() => page.setTrashTarget(null)}
        onConfirm={() => {
          if (page.trashTarget?.kind === 'project') {
            page.archiveMutation.mutate({
              id: page.trashTarget.id,
              archive: true,
              name: page.trashTarget.name,
            })
          }
        }}
        isPending={page.archiveMutation.isPending}
      />
      <DeleteProjectModal
        open={page.deleteTarget !== null}
        project={page.deleteTarget}
        onClose={() => page.setDeleteTarget(null)}
      />
      {renameTarget && (
        <RenameFolderDialog
          initialName={renameTarget.name}
          existingNames={page.folders.map((f) => f.name)}
          pending={page.renameFolderMutation.isPending}
          serverError={page.renameNameError}
          onCancel={() => {
            page.setRenameTarget(null)
            page.setRenameNameError(null)
          }}
          onSubmit={(name) => page.renameFolderMutation.mutate({ id: renameTarget.id, name })}
          onClearServerError={() => page.setRenameNameError(null)}
        />
      )}
      <DeleteFolderModal
        folder={page.folderDeleteTarget}
        onClose={() => page.setFolderDeleteTarget(null)}
        onConfirm={() => {
          if (page.folderDeleteTarget) {
            page.deleteFolderMutation.mutate(page.folderDeleteTarget.id)
          }
        }}
        isPending={page.deleteFolderMutation.isPending}
      />
    </div>
  )
}

type PageModel = ReturnType<typeof useProjectsPageModel>

function setRenameFromFolder(
  setRenameTarget: (v: { id: string; name: string } | null) => void,
  folder: ProjectFolder,
) {
  setRenameTarget({ id: folder.id, name: folder.name })
}

function setDeleteFromFolder(
  setFolderDeleteTarget: (v: { id: string; name: string } | null) => void,
  folder: ProjectFolder,
) {
  setFolderDeleteTarget({ id: folder.id, name: folder.name })
}

function ProjectsMainContent({
  location,
  page,
}: {
  location: ProjectsLocation
  page: PageModel
}) {
  if (!page.data) return null

  if (page.missingFolder) {
    return (
      <div className="rounded-md border border-border bg-muted/40 px-4 py-8 text-center space-y-3">
        <p className="text-sm font-semibold text-foreground">フォルダが見つかりません</p>
        <Link to={projectsLocationHref({ kind: 'all' })} className="text-sm text-primary underline">
          すべてのプロジェクトへ戻る
        </Link>
      </div>
    )
  }

  if (location.kind === 'trash') {
    if (page.data.items.length === 0) return <EmptyState kind="trash" />
    return (
      <TrashProjectList
        projects={page.data.items}
        onRestore={(id, name) => page.archiveMutation.mutate({ id, archive: false, name })}
        onPurgeNow={(id, name) => page.setDeleteTarget({ id, name })}
        archivePending={page.archiveMutation.isPending}
        canDeleteProject={page.canDeleteProject}
      />
    )
  }

  if (location.kind === 'all') {
    return (
      <>
        {page.data.items.length === 0 && !page.creatingFolder && (
          <EmptyState kind="active" onCreate={() => page.openCreateProject()} />
        )}
        <ActiveProjectsExplorer
          projects={page.data.items}
          onMoveToTrash={(id, name) => page.setTrashTarget({ kind: 'project', id, name })}
          archivePending={page.archiveMutation.isPending}
          onCreateInFolder={(folderId) => page.openCreateProject({ folderId })}
          creatingFolder={page.creatingFolder}
          newFolderName={page.newFolderName}
          onStartCreateFolder={() => page.setCreatingFolder(true)}
          onCancelCreateFolder={() => {
            page.setCreatingFolder(false)
            page.setNewFolderName('')
            page.setCreateNameError(null)
          }}
          onChangeNewFolderName={(v) => {
            page.setNewFolderName(v)
            page.setCreateNameError(null)
          }}
          onSubmitCreateFolder={() => {
            const name = page.newFolderName.trim()
            if (!name) return
            page.submitCreateFolder(name)
          }}
          createFolderPending={page.createFolderMutation.isPending}
          createNameError={page.createNameError}
          showFolderToolbar={
            !page.foldersUnavailable && (page.creatingFolder || page.folders.length === 0)
          }
        />
      </>
    )
  }

  if (location.kind === 'folder' && !page.currentFolder) return null

  return (
    <LocationProjectsList
      projects={page.filteredProjects}
      folders={page.folders}
      foldersUnavailable={page.foldersUnavailable}
      onMoveToTrash={(id, name) => page.setTrashTarget({ kind: 'project', id, name })}
      archivePending={page.archiveMutation.isPending}
    />
  )
}

function useProjectsPageModel(
  location: ProjectsLocation,
  setSearchParams: ReturnType<typeof useSearchParams>[1],
) {
  const { openCreateProject } = useAppShell()
  const queryClient = useQueryClient()
  const toast = useToast()
  const meTenantQ = useMeTenant()
  const canDeleteProject = meTenantQ.data?.plan === 'PRO'

  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
  const [trashTarget, setTrashTarget] = useState<TrashTarget | null>(null)
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [createNameError, setCreateNameError] = useState<string | null>(null)
  const [renameTarget, setRenameTarget] = useState<{ id: string; name: string } | null>(null)
  const [renameNameError, setRenameNameError] = useState<string | null>(null)
  const [folderDeleteTarget, setFolderDeleteTarget] = useState<{
    id: string
    name: string
  } | null>(null)
  const isCreatingFolderRef = useRef(false)

  const isTrash = location.kind === 'trash'

  const { data, isLoading, error } = useQuery(projectListQueryOptions(isTrash))

  const foldersQ = useQuery({
    queryKey: ['folders'],
    queryFn: listFolders,
    enabled: !isTrash,
  })

  const folders = foldersQ.data?.items ?? []
  const foldersUnavailable = !!foldersQ.error

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['projects'] })
    queryClient.invalidateQueries({ queryKey: ['folders'] })
  }

  const archiveMutation = useMutation({
    mutationFn: ({ id, archive }: { id: string; archive: boolean; name: string }) =>
      archive ? archiveProject(id) : unarchiveProject(id),
    onSuccess: (_, variables) => {
      invalidate()
      toast.success(
        variables.archive
          ? `「${variables.name}」を${TRASH_ACTION.toastMovedProject}`
          : `「${variables.name}」を${TRASH_ACTION.toastRestoredProject}`,
      )
      setTrashTarget(null)
    },
    onError: (err) => toast.error(toUserMessage(err, SUBMIT_ERROR_FALLBACK)),
  })

  const createFolderMutation = useMutation({
    mutationFn: (name: string) => createFolder({ name }),
    onSuccess: (folder) => {
      // 先にキャッシュへ載せてから location を変える。
      // invalidate 完了前に遷移すると currentFolder が空になり
      // 「フォルダが見つかりません」が一瞬フラッシュする。
      queryClient.setQueryData<ListFoldersResult>(['folders'], (prev) => {
        const item = { ...folder, activeProjectCount: 0 }
        if (!prev) return { items: [item] }
        if (prev.items.some((f) => f.id === folder.id)) return prev
        return { items: [...prev.items, item] }
      })
      setCreatingFolder(false)
      setNewFolderName('')
      setCreateNameError(null)
      toast.success('フォルダを作成しました')
      setSearchParams({ location: folder.id })
      void queryClient.invalidateQueries({ queryKey: ['folders'] })
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'CONFLICT') {
        setCreateNameError(FOLDER_NAME_CONFLICT_MESSAGE)
        return
      }
      toast.error(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    },
    onSettled: () => {
      isCreatingFolderRef.current = false
    },
  })

  const submitCreateFolder = (name: string) => {
    // React Query の isPending が描画へ反映される前の連打も同期的に遮断する。
    if (isCreatingFolderRef.current) return
    const normalized = normalizeFolderName(name)
    if (!normalized) return
    if (folders.some((f) => normalizeFolderName(f.name) === normalized)) {
      setCreateNameError(FOLDER_NAME_CONFLICT_MESSAGE)
      return
    }
    isCreatingFolderRef.current = true
    setCreateNameError(null)
    createFolderMutation.mutate(normalized)
  }

  const renameFolderMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updateFolder(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folders'] })
      setRenameTarget(null)
      setRenameNameError(null)
      toast.success('フォルダ名を変更しました')
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'CONFLICT') {
        setRenameNameError(FOLDER_NAME_CONFLICT_MESSAGE)
        return
      }
      toast.error(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    },
  })

  const deleteFolderMutation = useMutation({
    mutationFn: (id: string) => deleteFolder(id),
    onSuccess: () => {
      invalidate()
      setFolderDeleteTarget(null)
      toast.success('フォルダを削除しました（中のプロジェクトは未分類です）')
      setSearchParams({})
    },
    onError: (err) => toast.error(toUserMessage(err, SUBMIT_ERROR_FALLBACK)),
  })

  const currentFolder = useMemo(() => {
    if (location.kind !== 'folder') return null
    return folders.find((f) => f.id === location.folderId) ?? null
  }, [location, folders])

  const filteredProjects = useMemo(() => {
    const items = data?.items ?? []
    if (location.kind === 'folder') {
      return items.filter((p) => p.folderId === location.folderId)
    }
    if (location.kind === 'uncategorized') {
      return items.filter((p) => p.folderId === null || !folders.some((f) => f.id === p.folderId))
    }
    return items
  }, [data?.items, location, folders])

  const title = locationTitle(location, currentFolder)
  const count = isTrash
    ? (data?.items.length ?? null)
    : location.kind === 'all'
      ? (data?.items.length ?? null)
      : filteredProjects.length

  const createInCurrentLocation = () => {
    if (location.kind === 'folder') {
      openCreateProject({ folderId: location.folderId })
      return
    }
    if (location.kind === 'uncategorized') {
      openCreateProject({ folderId: null })
      return
    }
    openCreateProject()
  }

  const missingFolder =
    location.kind === 'folder' && !foldersQ.isLoading && !foldersUnavailable && !currentFolder

  return {
    openCreateProject,
    canDeleteProject,
    data,
    isLoading,
    error,
    folders,
    foldersUnavailable,
    currentFolder,
    filteredProjects,
    title,
    count,
    missingFolder,
    createInCurrentLocation,
    archiveMutation,
    createFolderMutation,
    submitCreateFolder,
    renameFolderMutation,
    deleteFolderMutation,
    deleteTarget,
    setDeleteTarget,
    trashTarget,
    setTrashTarget,
    creatingFolder,
    setCreatingFolder,
    newFolderName,
    setNewFolderName,
    createNameError,
    setCreateNameError,
    renameTarget,
    setRenameTarget,
    renameNameError,
    setRenameNameError,
    folderDeleteTarget,
    setFolderDeleteTarget,
  }
}

function locationTitle(location: ProjectsLocation, folder: ProjectFolder | null): string {
  switch (location.kind) {
    case 'all':
      return 'すべてのプロジェクト'
    case 'trash':
      return TRASH_ACTION.tab
    case 'uncategorized':
      return '未分類'
    case 'folder':
      return folder?.name ?? 'フォルダ'
  }
}

function LocationToolbar({
  title,
  count,
  location,
  onCreateProject,
  onStartCreateFolder,
  onRenameFolder,
  onDeleteFolder,
}: {
  title: string
  count: number | null
  location: ProjectsLocation
  onCreateProject?: (() => void) | undefined
  onStartCreateFolder?: (() => void) | undefined
  onRenameFolder?: (() => void) | undefined
  onDeleteFolder?: (() => void) | undefined
}) {
  return (
    <header className="flex items-end justify-between gap-4 flex-wrap border-b border-border pb-3">
      <div className="min-w-0">
        <h1 className="text-xl md:text-2xl font-bold tracking-tight text-foreground truncate">
          {title}
        </h1>
        {count !== null && (
          <p className="mt-0.5 text-xs text-muted-foreground">
            <span className="editorial-num font-medium text-foreground">
              {count.toLocaleString('ja-JP')}
            </span>
            {location.kind === 'trash' ? ' 件' : ' 件のプロジェクト'}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {onStartCreateFolder && (
          <button
            type="button"
            onClick={onStartCreateFolder}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-border bg-card hover:bg-accent transition-colors"
          >
            フォルダを追加
          </button>
        )}
        {onRenameFolder && (
          <button
            type="button"
            onClick={onRenameFolder}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-border bg-card hover:bg-accent transition-colors"
          >
            改名
          </button>
        )}
        {onDeleteFolder && (
          <button
            type="button"
            onClick={onDeleteFolder}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-border bg-card hover:bg-accent text-muted-foreground hover:text-destructive transition-colors"
          >
            フォルダを削除
          </button>
        )}
        {onCreateProject && (
          <button
            type="button"
            onClick={onCreateProject}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold text-white bg-gradient-primary shadow-sm hover:opacity-90 transition-opacity"
          >
            <span aria-hidden>＋</span>
            新規プロジェクト
          </button>
        )}
      </div>
    </header>
  )
}

function EmptyState({
  kind,
  onCreate,
}: {
  kind: 'active' | 'trash'
  onCreate?: (() => void) | undefined
}) {
  return (
    <div className="border border-dashed border-border rounded-md py-14 text-center bg-muted/30">
      <p className="text-sm font-semibold text-foreground">
        {kind === 'active' ? 'まだプロジェクトはありません' : 'ゴミ箱は空です'}
      </p>
      <p className="text-xs text-muted-foreground mt-2 max-w-sm mx-auto">
        {kind === 'active'
          ? '案件単位でプロジェクトを作って、動画ファイルや共有 URL をひとまとめに管理できます。'
          : '削除したプロジェクトがここに表示されます。30日後に自動で完全に消えます。'}
      </p>
      {kind === 'active' && onCreate && (
        <button
          type="button"
          onClick={onCreate}
          className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-white bg-gradient-primary shadow-sm hover:opacity-90 transition-opacity"
        >
          <span aria-hidden>＋</span>
          最初のプロジェクトを作成
        </button>
      )}
    </div>
  )
}

function TrashProjectList({
  projects,
  onRestore,
  onPurgeNow,
  archivePending,
  canDeleteProject,
}: {
  projects: Project[]
  onRestore: (id: string, name: string) => void
  onPurgeNow: (id: string, name: string) => void
  archivePending: boolean
  canDeleteProject: boolean
}) {
  return (
    <ul className="flex flex-col divide-y divide-border border border-border rounded-md bg-card overflow-hidden">
      {projects.map((p) => (
        <TrashProjectRow
          key={p.id}
          project={p}
          onRestore={() => onRestore(p.id, p.name)}
          onPurgeNow={() => onPurgeNow(p.id, p.name)}
          archivePending={archivePending}
          canDeleteProject={canDeleteProject}
        />
      ))}
    </ul>
  )
}

function TrashProjectRow({
  project,
  onRestore,
  onPurgeNow,
  archivePending,
  canDeleteProject,
}: {
  project: Project
  onRestore: () => void
  onPurgeNow: () => void
  archivePending: boolean
  canDeleteProject: boolean
}) {
  const queryClient = useQueryClient()
  const prefetchDetail = () => {
    queryClient.prefetchQuery({
      queryKey: ['projects', project.id],
      queryFn: () => getProject(project.id),
    })
    queryClient.prefetchQuery({
      queryKey: ['files', project.id],
      queryFn: () => listFiles(project.id),
    })
  }
  return (
    <li className="group">
      <div className="flex flex-col md:flex-row md:items-center">
        <Link
          to={`/projects/${project.id}`}
          onMouseEnter={prefetchDetail}
          onFocus={prefetchDetail}
          className="block min-w-0 flex-1 py-2.5 px-3 md:pr-2"
          aria-label={`${project.name} を開く`}
        >
          <div className="min-w-0">
            <h2 className="text-sm font-semibold tracking-tight truncate text-foreground group-hover:text-primary transition-colors">
              {project.name}
            </h2>
            <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground editorial-num flex-wrap">
              <span>更新 {formatDate(project.updatedAt)}</span>
              <span aria-hidden className="text-muted-foreground/40">
                ·
              </span>
              <span>作成 {formatDate(project.createdAt)}</span>
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-1.5 px-3 pb-2.5 md:pb-0 md:pl-0 md:pr-3 shrink-0 border-t border-border/60 md:border-t-0 pt-2 md:pt-0">
          <button
            type="button"
            onClick={onRestore}
            disabled={archivePending}
            className="text-[11px] font-medium px-2 py-1 border border-border bg-card rounded-md hover:bg-accent transition-colors disabled:opacity-50"
          >
            {TRASH_ACTION.restore}
          </button>
          {canDeleteProject ? (
            <button
              type="button"
              onClick={onPurgeNow}
              className="text-[11px] font-medium px-2 py-1 border border-destructive/30 text-destructive bg-card rounded-md hover:bg-destructive/5 transition-colors"
              aria-label={`${project.name} を今すぐ完全に消す`}
            >
              {TRASH_ACTION.purgeNow}
            </button>
          ) : (
            <button
              type="button"
              disabled
              title="プロジェクトの完全削除は有料プラン専用です"
              className="text-[11px] font-medium px-2 py-1 border border-border text-muted-foreground rounded-md opacity-60 cursor-not-allowed"
            >
              {TRASH_ACTION.purgeNow}（PRO）
            </button>
          )}
        </div>
      </div>
    </li>
  )
}

function RenameFolderDialog({
  initialName,
  existingNames,
  pending,
  serverError,
  onCancel,
  onSubmit,
  onClearServerError,
}: {
  initialName: string
  existingNames: string[]
  pending: boolean
  serverError: string | null
  onCancel: () => void
  onSubmit: (name: string) => void
  onClearServerError: () => void
}) {
  const [name, setName] = useState(initialName)
  const normalized = normalizeFolderName(name)
  const initialNormalized = normalizeFolderName(initialName)
  const isDuplicate =
    normalized.length > 0 &&
    normalized !== initialNormalized &&
    existingNames.some((n) => normalizeFolderName(n) === normalized)
  const inlineError = isDuplicate ? FOLDER_NAME_CONFLICT_MESSAGE : serverError
  const canSubmit = normalized.length > 0 && !isDuplicate && !pending

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-background border border-border rounded-lg shadow-lg p-6 w-full max-w-md space-y-4">
        <h2 className="text-lg font-semibold">フォルダ名を変更</h2>
        <div className="space-y-1.5">
          <input
            type="text"
            maxLength={100}
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              onClearServerError()
            }}
            aria-invalid={inlineError ? true : undefined}
            className={`w-full px-3 py-2 border rounded-md bg-background ${
              inlineError ? 'border-destructive' : 'border-border'
            }`}
            // biome-ignore lint/a11y/noAutofocus: モーダル内のメイン入力
            autoFocus
          />
          {inlineError && <p className="text-xs text-destructive">{inlineError}</p>}
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="px-4 py-2 border border-border rounded-md font-medium"
          >
            キャンセル
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onSubmit(normalized)}
            className="px-4 py-2 bg-foreground text-background rounded-md font-medium disabled:opacity-50"
          >
            {pending ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="border border-destructive/40 bg-destructive/5 rounded-md p-4 text-sm text-destructive">
      {message}
    </div>
  )
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}
